import { useEffect, useState } from "react";
import { api, formatApiError, formatDate, formatDateTime, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Users, HandHeart, Baby, MapPin, Share2, AlertCircle, ClipboardList, ArrowUpRight, PhoneIncoming, Bell, Inbox, Clock, BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import OnboardingWidget from "@/components/OnboardingWidget";
import EmailHealthWidget from "@/components/EmailHealthWidget";
import DashboardSwitcher from "@/components/DashboardSwitcher";

const StatCard = ({ icon: Icon, label, value, accent = "navy", to, testId, delta }) => {
  const accents = {
    navy: "bg-[#1E3A8A]",
    sage: "bg-[#7E937D]",
    sun: "bg-[#F59E0B]",
  };
  const deltaColor =
    delta == null || delta === 0
      ? "text-[#1E3A8A]"
      : delta > 0
      ? "text-[#166534]"
      : "text-[#991B1B]";
  const deltaLabel =
    delta == null
      ? null
      : delta === 0
      ? "no change vs last week"
      : `${delta > 0 ? "+" : ""}${delta} vs last week`;
  const card = (
    <div
      data-testid={testId}
      className="bg-white rounded-2xl border border-[#E5E5E0] p-5 hw-card-hover h-full"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`${accents[accent]} w-10 h-10 rounded-xl flex items-center justify-center text-white`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        {to && <ArrowUpRight className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />}
      </div>
      <div className="text-3xl font-display font-semibold text-[#0F172A]">{value}</div>
      <div className="text-sm text-[#334155] mt-1">{label}</div>
      {deltaLabel && (
        <div className={`text-xs mt-1 ${deltaColor}`} data-testid={testId ? `${testId}-delta` : undefined}>
          {deltaLabel}
        </div>
      )}
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [pendingReferrals, setPendingReferrals] = useState([]);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(() => localStorage.getItem("hw_dashboard_range") || "all_time");

  const loadReferrals = () => {
    api.get("/intake-requests", { params: { status: "pending" } })
      .then((r) => setPendingReferrals(r.data || []))
      .catch(() => setPendingReferrals([]));
  };

  // Re-fetch stats whenever the user changes the time range.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    localStorage.setItem("hw_dashboard_range", range);
    Promise.all([
      api.get("/dashboard/stats", { params: { range } }),
      api.get("/dashboard/reminders-due").catch(() => ({ data: [] })),
      api.get("/intake-requests", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      api.get("/timesheets/pending").catch(() => ({ data: [] })),
    ])
      .then(([s, r, ir, pt]) => {
        if (!mounted) return;
        setStats(s.data);
        setReminders(r.data || []);
        setPendingReferrals(ir.data || []);
        setPendingTimesheets(pt.data || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [range]);

  const accept = async (req) => {
    try {
      const { data } = await api.post(`/intake-requests/${req.id}/accept`, { decision_notes: null });
      toast.success(`Care Seeker ${data.display_id} created`);
      loadReferrals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
    }
  };

  const reject = async (req) => {
    const reason = window.prompt(`Reject the referral for ${req.seeker_first_name} ${req.seeker_last_name}?\n\nOptional reason (visible in audit log only):`);
    if (reason === null) return; // cancelled
    try {
      await api.post(`/intake-requests/${req.id}/reject`, { decision_notes: reason.trim() || null });
      toast.success("Referral rejected");
      loadReferrals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="hw-eyebrow mb-2">Today at HarborWell</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
          Welcome back, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          A quick look at the community you're caring for today. Take a moment, breathe,
          and pick up where you left off.
        </p>
      </div>

      <DashboardSwitcher />

      <OnboardingWidget />

      {/* Time range picker — controls all range-aware widgets below. */}
      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="dashboard-range-picker">
        <div className="text-xs uppercase tracking-wider text-[#1E3A8A] font-semibold">
          Show stats for
        </div>
        <div className="inline-flex rounded-full bg-white border border-[#E5E5E0] p-1 shadow-sm">
          {[
            { id: "today", label: "Today" },
            { id: "this_week", label: "This week" },
            { id: "this_month", label: "This month" },
            { id: "all_time", label: "All time" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setRange(opt.id)}
              data-testid={`dashboard-range-${opt.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                range === opt.id
                  ? "bg-[#1E3A8A] text-white"
                  : "text-[#334155] hover:bg-[#F9F8F6]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E5E0] rounded-2xl h-32 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          {/* Timesheets pending review (shown to admin/ED + supervisors with direct reports) */}
          {pendingTimesheets.length > 0 && (
            <div className="bg-[#DBEAFE] border border-[#93C5FD] rounded-2xl p-5" data-testid="pending-timesheets-panel">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center">
                  <Clock className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold text-[#1E40AF]">Timesheets pending your review</h2>
                  <div className="text-xs text-[#1E40AF]/80">{pendingTimesheets.length} timesheet{pendingTimesheets.length > 1 ? "s" : ""} submitted, awaiting your approval</div>
                </div>
              </div>
              <ul className="space-y-2" data-testid="pending-timesheets-list">
                {pendingTimesheets.slice(0, 5).map((ts) => {
                  const approveTs = async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    try {
                      await api.post(`/timesheets/${ts.id}/approve`);
                      setPendingTimesheets((prev) => prev.filter((x) => x.id !== ts.id));
                      toast.success(`Approved · ${ts.user_name || "timesheet"}`);
                    } catch (err) { toast.error(formatApiError(err)); }
                  };
                  const rejectTs = async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    const notes = window.prompt("What needs to change? (notes are emailed to the submitter)", "");
                    if (notes === null) return;
                    if (!notes.trim()) { toast.error("Please provide a short note"); return; }
                    try {
                      await api.post(`/timesheets/${ts.id}/reject`, { reviewer_notes: notes.trim() });
                      setPendingTimesheets((prev) => prev.filter((x) => x.id !== ts.id));
                      toast.success(`Returned · ${ts.user_name || "timesheet"}`);
                    } catch (err) { toast.error(formatApiError(err)); }
                  };
                  return (
                    <li key={ts.id} className="bg-white/70 hover:bg-white border border-[#93C5FD]/60 rounded-xl transition-colors">
                      <div className="flex items-center gap-3 px-4 py-2.5">
                        <Link
                          to="/timesheets"
                          data-testid={`pending-timesheet-${ts.id}`}
                          className="flex-1 min-w-0"
                        >
                          <div className="font-medium text-[#0F172A] truncate">
                            {ts.user_name || "A staff member"} · {ts.total_hours?.toFixed?.(2) ?? ts.total_hours}h
                          </div>
                          <div className="text-xs text-[#1E40AF]">
                            Week of {formatDate(ts.week_start_date)}
                            {ts.submitted_at && ` · submitted ${formatDateTime(ts.submitted_at)}`}
                          </div>
                        </Link>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={approveTs}
                            data-testid={`pending-ts-approve-${ts.id}`}
                            title="Approve"
                            className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium text-[#166534] hover:bg-[#E8ECE7] border border-transparent hover:border-[#7E937D]/40 inline-flex items-center gap-1"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={rejectTs}
                            data-testid={`pending-ts-reject-${ts.id}`}
                            title="Return for changes"
                            className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium text-[#991B1B] hover:bg-[#FEE2E2] border border-transparent hover:border-[#FCA5A5]"
                          >
                            Return
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
                {pendingTimesheets.length > 5 && (
                  <li>
                    <Link to="/timesheets" className="text-xs text-[#1E40AF] font-medium hover:underline" data-testid="pending-timesheets-view-all">
                      View all {pendingTimesheets.length} →
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Reminders due — shown next so it's surfaced early */}
          {reminders.length > 0 && (
            <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-2xl p-5" data-testid="reminders-panel">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-xl bg-[#F59E0B] text-white flex items-center justify-center">
                  <Bell className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold text-[#92400E]">Reminders due today</h2>
                  <div className="text-xs text-[#92400E]/80">{reminders.length} Care Seeker{reminders.length > 1 ? "s" : ""} flagged for follow-up</div>
                </div>
              </div>
              <ul className="space-y-2" data-testid="reminders-list">
                {reminders.map((r) => {
                  const isOverdue = r.reminder_date && r.reminder_date < new Date().toISOString().slice(0, 10);
                  const updateReminder = async (newDate, label) => {
                    try {
                      await api.patch(`/care-seekers/${r.id}`, { reminder_date: newDate });
                      setReminders((prev) => prev.filter((x) => x.id !== r.id));
                      toast.success(label);
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  };
                  const snooze7 = (e) => {
                    e.preventDefault(); e.stopPropagation();
                    const d = new Date();
                    d.setDate(d.getDate() + 7);
                    updateReminder(d.toISOString().slice(0, 10), `Snoozed for 7 days · ${r.preferred_name || r.first_name}`);
                  };
                  const dismiss = (e) => {
                    e.preventDefault(); e.stopPropagation();
                    updateReminder(null, `Reminder cleared · ${r.preferred_name || r.first_name}`);
                  };
                  return (
                    <li key={r.id} className="bg-white/70 hover:bg-white border border-[#FCD34D]/60 rounded-xl transition-colors group">
                      <div className="flex items-center gap-3 px-4 py-2.5">
                        <Link
                          to={`/care-seekers/${r.id}`}
                          data-testid={`reminder-${r.id}`}
                          className="flex-1 min-w-0 flex items-center gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-[#0F172A] truncate">
                              {r.preferred_name || r.first_name} {r.last_name}
                            </div>
                            {r.reminder_note && <div className="text-xs text-[#334155] truncate">{r.reminder_note}</div>}
                          </div>
                          <span className="text-[10px] uppercase tracking-wider text-[#92400E] font-medium whitespace-nowrap">
                            {isOverdue ? "Overdue · " : ""}
                            {formatDate(r.reminder_date)}
                          </span>
                        </Link>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={snooze7}
                            data-testid={`reminder-snooze-${r.id}`}
                            title="Snooze 7 days"
                            className="px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium text-[#92400E] hover:bg-[#FEF3C7] border border-transparent hover:border-[#FCD34D]"
                          >
                            Snooze 7d
                          </button>
                          <button
                            type="button"
                            onClick={dismiss}
                            data-testid={`reminder-dismiss-${r.id}`}
                            title="Dismiss reminder"
                            className="px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium text-[#991B1B] hover:bg-[#FEE2E2] border border-transparent hover:border-[#FCA5A5]"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* New partner submissions — admin/ED action alert */}
          {(stats?.partner_submissions?.needs_action ?? 0) > 0 && (
            <Link
              to="/admin/partner-submissions?status=pending_review"
              data-testid="partner-submissions-alert"
              className="block bg-[#FFE4E6] border border-[#FCA5A5] rounded-2xl p-5 hover:border-[#DC2626] transition-colors"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#DC2626] text-white flex items-center justify-center">
                    <Inbox className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-[#991B1B]">
                      {stats.partner_submissions.needs_action} new partner submission
                      {stats.partner_submissions.needs_action === 1 ? "" : "s"} awaiting review
                    </h2>
                    <div className="text-xs text-[#991B1B]/80">
                      {(stats.partner_submissions.info_requested ?? 0) > 0 &&
                        `${stats.partner_submissions.info_requested} info requested · `}
                      Click to open the queue
                    </div>
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-[#991B1B]" />
              </div>
            </Link>
          )}

          {pendingReferrals.length > 0 && (
            <div className="bg-[#E0E7FF] border border-[#A5B4FC] rounded-2xl p-5" data-testid="pending-referrals-panel">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center">
                    <Inbox className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-[#1E3A8A]">Inbound referrals</h2>
                    <div className="text-xs text-[#1E3A8A]/80">
                      {pendingReferrals.length} partner referral{pendingReferrals.length > 1 ? "s" : ""} waiting for review
                    </div>
                  </div>
                </div>
                <Link
                  to="/referrals?dir=inbound"
                  data-testid="view-all-inbound"
                  className="text-xs text-[#1E3A8A] font-medium inline-flex items-center gap-1 hover:underline"
                >
                  View all inbound <ArrowUpRight className="w-3 h-3" />
                </Link>
              </div>
              <ul className="space-y-2" data-testid="pending-referrals-list">
                {pendingReferrals.map((req) => (
                  <li
                    key={req.id}
                    data-testid={`pending-referral-${req.id}`}
                    className="bg-white border border-[#C7D2FE] rounded-xl px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#0F172A]">
                            {req.seeker_preferred_name || req.seeker_first_name} {req.seeker_last_name}
                          </span>
                          {req.urgency && req.urgency !== "medium" && (
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                              req.urgency === "critical" ? "bg-[#FEE2E2] text-[#991B1B]" :
                              req.urgency === "high" ? "bg-[#FEF3C7] text-[#92400E]" :
                              "bg-[#F1F5F9] text-[#475569]"
                            }`}>{req.urgency}</span>
                          )}
                        </div>
                        <div className="text-xs text-[#334155] mt-0.5">
                          {req.primary_need ? <strong>{req.primary_need}</strong> : "—"}
                          {req.seeker_county ? ` · ${req.seeker_county} County` : ""}
                          {req.seeker_phone ? ` · ${req.seeker_phone}` : ""}
                        </div>
                        <div className="text-[11px] text-[#1E3A8A] mt-1">
                          From <strong>{req.referrer_name}</strong>
                          {req.referrer_organization ? ` at ${req.referrer_organization}` : ""}
                          {" · "}{formatDateTime(req.created_at)}
                        </div>
                        {req.notes && (
                          <p className="text-xs text-[#334155] mt-1.5 whitespace-pre-wrap line-clamp-2">{req.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => reject(req)}
                          data-testid={`reject-${req.id}`}
                          className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#991B1B] hover:bg-[#FEE2E2]"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => accept(req)}
                          data-testid={`accept-${req.id}`}
                          className="text-xs px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554]"
                        >
                          Accept → Create CSK
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Order matches the spec in HarborWell Connect Updates doc verbatim. */}
            <StatCard icon={Users} label="Total Care Seekers served" value={stats?.total_care_seekers ?? 0} to="/care-seekers" testId="stat-care-seekers" />
            <StatCard icon={Users} label="Active Care Seekers" value={stats?.active_care_seekers ?? 0} accent="navy" to="/care-seekers?status=active&assigned_to=all" testId="stat-active" />
            <StatCard icon={Share2} label="Referrals completed" value={stats?.referrals_completed ?? 0} accent="sage" to="/referrals" testId="stat-referrals" />
            <StatCard icon={ClipboardList} label="Active follow-ups" value={stats?.active_followups ?? 0} accent="sage" to="/tasks" testId="stat-followups" />
            <StatCard icon={AlertCircle} label="Overdue tasks" value={stats?.overdue_tasks ?? 0} accent="sun" to="/tasks?overdue=1" testId="stat-overdue" />
            <StatCard
              icon={PhoneIncoming}
              label={
                range === "today" ? "Hotline calls today"
                : range === "this_week" ? "Hotline calls this week"
                : range === "this_month" ? "Hotline calls this month"
                : "Hotline calls (all time)"
              }
              value={stats?.hotline_in_range ?? stats?.hotline_this_week ?? 0}
              delta={range === "this_week" ? (stats?.hotline_this_week ?? 0) - (stats?.hotline_last_week ?? 0) : undefined}
              accent="navy"
              to="/care-seekers?channel=hotline_call"
              testId="stat-hotline-week"
            />
            <StatCard icon={HandHeart} label="Outreach contacts" value={stats?.outreach_contacts ?? 0} accent="sun" to="/outreach" testId="stat-outreach" />
            <StatCard icon={Baby} label="Maternal visits" value={stats?.maternal_visits ?? 0} accent="sage" to="/maternal" testId="stat-maternal" />
            <StatCard icon={BookOpen} label="Community Partner Resources in Directory" value={stats?.resources_total ?? 0} accent="sage" to="/resources" testId="stat-resources-total" />
            <StatCard icon={MapPin} label="Total Counties Served" value={stats?.counties_served ?? 0} accent="navy" testId="stat-counties" />
          </div>

          {(user?.role === "admin" || user?.role === "executive_director") && <PayPeriodHoursWidget />}
          {user?.role === "admin" && (
            <div className="mb-4" data-testid="dashboard-email-health-slot">
              <EmailHealthWidget />
            </div>
          )}

          {/* "How they reached us" — moved up per dashboard reorg */}
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="intake-channels-panel">
            <div className="flex items-center gap-2 mb-4">
              <PhoneIncoming className="w-4 h-4 text-[#1E3A8A]" />
              <h2 className="font-display text-xl font-semibold text-[#0F172A]">How they reached us</h2>
            </div>
            {(!stats?.intake_channels || stats.intake_channels.length === 0) ? (
              <EmptyState message="Intake channels appear as Care Seekers are added." />
            ) : (
              <ul className="space-y-3" data-testid="intake-channels-list">
                {stats.intake_channels.map((c) => {
                  const max = stats.intake_channels[0]?.count || 1;
                  return (
                    <li key={c.channel}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#334155] capitalize">{(c.channel || "unknown").replace(/_/g, " ")}</span>
                        <span className="font-medium text-[#0F172A]">{c.count}</span>
                      </div>
                      <div className="h-1.5 bg-[#E0E7FF] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#1E3A8A]"
                          style={{ width: `${Math.min(100, (c.count / max) * 100)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const EmptyState = ({ message }) => (
  <div className="text-center py-8 text-sm text-[#1E3A8A]">{message}</div>
);


function PayPeriodHoursWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/reports/pay-period-hours")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => { if (mounted) setData(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl h-40 animate-pulse" />
    );
  }
  if (!data || !data.rows?.length) return null;

  const fmt = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      month: "short", day: "numeric", timeZone: "UTC",
    });
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid="pay-period-hours-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-[#0F172A]">Hours this pay period</h2>
          <p className="text-xs text-[#334155] mt-0.5">
            {fmt(data.date_from)} – {fmt(data.date_to)} · expected {data.expected_hours}h ·
            {" "}<Link to="/reports" className="text-[#1E3A8A] font-medium hover:underline" data-testid="pay-period-open-reports">Open reports →</Link>
          </p>
        </div>
      </div>
      <ul className="space-y-2.5" data-testid="pay-period-hours-list">
        {data.rows.map((r) => {
          const pct = data.expected_hours > 0
            ? Math.min(150, Math.round((r.total_hours / data.expected_hours) * 100))
            : 0;
          const delta = r.total_hours - data.expected_hours;
          // Color logic: red >10h under, amber 4-10h under, green within 4h
          // either way, blue if >4h over (visible but not "ok green").
          const tone =
            delta <= -10 ? "red"
            : delta < -4 ? "amber"
            : delta > 4 ? "blue"
            : "green";
          const bar = {
            red:   "bg-[#DC2626]",
            amber: "bg-[#F59E0B]",
            green: "bg-[#166534]",
            blue:  "bg-[#1E3A8A]",
          }[tone];
          const chip = {
            red:   "bg-[#FEE2E2] text-[#991B1B]",
            amber: "bg-[#FEF3C7] text-[#92400E]",
            green: "bg-[#E8ECE7] text-[#166534]",
            blue:  "bg-[#DBEAFE] text-[#1E40AF]",
          }[tone];
          const deltaLabel =
            delta === 0 ? "On target"
            : delta > 0 ? `+${delta.toFixed(2)}h over`
            : `${delta.toFixed(2)}h under`;
          return (
            <li key={r.user_id} className="flex items-center gap-3 flex-wrap" data-testid={`pay-period-row-${r.user_id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-[#0F172A] truncate">{r.name}</span>
                  <span className="text-xs text-[#334155] whitespace-nowrap tabular-nums">
                    <strong className="text-[#0F172A]">{r.total_hours.toFixed(2)}</strong>
                    <span className="text-[#1E3A8A]"> / {data.expected_hours}h</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div
                    className={`h-full ${bar} transition-all`}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                    data-testid={`pay-period-bar-${r.user_id}`}
                  />
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium ${chip}`} data-testid={`pay-period-delta-${r.user_id}`}>
                    {deltaLabel}
                  </span>
                  <span className="text-[10px] text-[#1E3A8A]/70 capitalize">
                    {ROLE_LABELS[r.role] || r.role?.replace(/_/g, " ")}
                  </span>
                  {r.status_counts.submitted > 0 && (
                    <span className="text-[10px] text-[#1E40AF]">
                      · {r.status_counts.submitted} pending review
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
